'use client';
import React, { useRef, useEffect } from 'react';
import { Bell, Check, Loader2, UserPlus, Heart, MessageCircle, Bookmark } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { getNotificationMessage } from '@/lib/notifications';
import type { Notification } from '@/lib/notifications';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function NotificationIcon({ type }: { type: Notification['notification_type'] }) {
  const base = 'w-7 h-7 rounded-full flex items-center justify-center shrink-0';
  switch (type) {
    case 'follow':
      return <div className={`${base} bg-blue-50`}><UserPlus size={13} className="text-blue-500" /></div>;
    case 'like':
      return <div className={`${base} bg-red-50`}><Heart size={13} className="text-red-500" /></div>;
    case 'comment': case'reply':
      return <div className={`${base} bg-charcoal-100`}><MessageCircle size={13} className="text-charcoal-600" /></div>;
    case 'save':
      return <div className={`${base} bg-gold-50`}><Bookmark size={13} className="text-gold-500" /></div>;
    default:
      return <div className={`${base} bg-cream-200`}><Bell size={13} className="text-charcoal-500" /></div>;
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function NotificationDropdown({ open, onClose }: Props) {
  const { notifications, unreadCount, loading, markAllRead, markRead } = useNotifications();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-cream-300 shadow-xl overflow-hidden fade-in z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-200">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-charcoal-900" style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.05em' }}>
            NOTIFICATIONS
          </h3>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-charcoal-900 text-white text-xs font-semibold rounded-full leading-none">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1 text-xs text-charcoal-400 hover:text-charcoal-700 transition-colors"
          >
            <Check size={11} />
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="max-h-[360px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 size={18} className="animate-spin text-charcoal-400" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-10 text-center">
            <Bell size={22} className="mx-auto text-charcoal-300 mb-2" />
            <p className="text-sm text-charcoal-400">No notifications yet</p>
            <p className="text-xs text-charcoal-300 mt-1">Activity will appear here</p>
          </div>
        ) : (
          <ul>
            {notifications.map((n) => (
              <li
                key={n.id}
                onClick={() => !n.is_read && markRead(n.id)}
                className={`flex items-start gap-3 px-4 py-3 border-b border-cream-100 last:border-0 cursor-pointer transition-colors ${
                  n.is_read ? 'bg-white hover:bg-cream-50' : 'bg-cream-50 hover:bg-cream-100'
                }`}
              >
                {/* Actor avatar or type icon */}
                {n.actor?.avatar_url ? (
                  <img
                    src={n.actor.avatar_url}
                    alt={n.actor.display_name || ''}
                    className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5"
                  />
                ) : (
                  <div className="mt-0.5">
                    <NotificationIcon type={n.notification_type} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-charcoal-800 leading-snug">{getNotificationMessage(n)}</p>
                  <p className="text-xs text-charcoal-400 mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
                {!n.is_read && (
                  <div className="w-2 h-2 rounded-full bg-charcoal-900 shrink-0 mt-1.5" />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
